import { useState } from "react";
import { AlertTriangle, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CARD_AUTH_NOTE, CARD_AUTH_TEXT, ConsentCheck } from "@/components/GpsConsentScreen";

const exp = (c) => `${String(c.exp_month).padStart(2, "0")}/${String(c.exp_year).slice(-2)}`;

// Card setup (add or replace). Requires the standing card authorization tick if not on file.
export const CardSetupButton = ({ label, outline = false, testId = "add-payment-button" }) => {
  const { user } = useAuth();
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const need = !user.card_authorized;
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/setup", { origin_url: window.location.origin, card_authorization: need ? agree : false });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="space-y-3">
      {need && (
        <div>
          <ConsentCheck testId="card-authorization-checkbox" checked={agree} onChange={setAgree}>{CARD_AUTH_TEXT}</ConsentCheck>
          <p className="mt-1.5 pl-7 text-xs text-zinc-500">{CARD_AUTH_NOTE}</p>
        </div>
      )}
      <button data-testid={testId} onClick={start} disabled={busy || (need && !agree)} className={`${outline ? "btn-outline-gold" : "btn-gold"} !py-2.5`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {label}
      </button>
    </div>
  );
};

export const CardOnFile = () => {
  const { user } = useAuth();
  const c = user.card;
  if (user.has_valid_card) {
    return (
      <div data-testid="payment-verified-badge" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-300">
        <span className="flex items-center gap-3"><ShieldCheck className="h-5 w-5" />
          <span data-testid="card-summary"><span className="capitalize">{c.brand}</span> •••• {c.last4} · exp {exp(c)} · {c.funding}</span></span>
        <CardSetupButton label="Replace card" outline testId="replace-card-button" />
      </div>
    );
  }
  return (
    <div data-testid="payment-unverified-banner" className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
        <div className="flex-1 space-y-3">
          <p className="font-semibold text-amber-100" data-testid="payment-required-message">{user.card_message}</p>
          {c && <p data-testid="card-summary" className="text-xs text-zinc-400">On file: <span className="capitalize">{c.brand}</span> •••• {c.last4} · exp {exp(c)}</p>}
          <p className="text-sm text-zinc-400">Save a credit or debit card on our secure card page (prepaid cards aren't accepted). Fare estimates stay visible.</p>
          <CardSetupButton label={c ? "Add a new card" : "Add your card"} />
        </div>
      </div>
    </div>
  );
};
